(() => {
  if (customElements.get('loop-player')) return;

  const AC = window.AudioContext || window.webkitAudioContext;
  const buffers = new Map(); // url -> Promise<AudioBuffer>
  let ctx = null;
  let audio = null;
  let current = null; // { row, stop, progress }
  let raf = 0;

  const getCtx = () => {
    if (!ctx) {
      ctx = new AC();
      // iOS : joue même quand le bouton silencieux est activé
      try {
        if (navigator.audioSession) navigator.audioSession.type = 'playback';
      } catch (e) {}
    }
    return ctx;
  };

  // Les loops sont téléchargées puis décodées une seule fois (boucle sans blanc via Web Audio)
  const loadBuffer = (url) => {
    if (!buffers.has(url)) {
      const promise = fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(res.status);
          return res.arrayBuffer();
        })
        .then((data) => new Promise((resolve, reject) => getCtx().decodeAudioData(data, resolve, reject)));
      promise.catch(() => buffers.delete(url));
      buffers.set(url, promise);
    }
    return buffers.get(url);
  };

  const setState = (row, state) => {
    row.dataset.state = state;
    row.querySelector('button').setAttribute('aria-pressed', state === 'stopped' ? 'false' : 'true');
  };

  const tick = () => {
    if (!current || !current.progress) return;
    current.row.style.setProperty('--dlp-progress', current.progress());
    raf = requestAnimationFrame(tick);
  };

  const started = (session) => {
    if (current !== session) return;
    setState(session.row, 'playing');
    tick();
  };

  // Le son s'arrête dès que le player (ou la vignette) sort complètement de l'écran au scroll
  let watched = null;
  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && entry.target === watched) stop();
          });
        })
      : null;
  const watch = (el) => {
    if (io && watched) io.unobserve(watched);
    watched = el;
    if (io && el) io.observe(el);
  };

  // Une seule chose qui sonne à la fois : lancer une loop met en pause les vidéos avec du son
  const audible = (media) => !media.muted && media.volume > 0;
  const pauseVideos = () => {
    document.querySelectorAll('video').forEach((video) => {
      if (!video.paused && audible(video)) video.pause();
    });
    document.querySelectorAll('iframe').forEach((frame) => {
      const win = frame.contentWindow;
      if (!win) return;
      if (/youtube/i.test(frame.src)) win.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      else if (/vimeo/i.test(frame.src)) win.postMessage('{"method":"pause"}', '*');
    });
  };

  const stop = () => {
    if (!current) return;
    const session = current;
    current = null;
    watch(null);
    cancelAnimationFrame(raf);
    if (session.stop) session.stop();
    session.row.style.setProperty('--dlp-progress', 0);
    setState(session.row, 'stopped');
  };

  const playWithElement = (session, url, loop) => {
    if (!audio) {
      audio = new Audio();
      audio.preload = 'none';
    }
    audio.loop = loop;
    audio.src = url;
    session.stop = () => {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    };
    session.progress = () => (audio.duration ? audio.currentTime / audio.duration : 0);
    audio.onended = () => {
      if (current === session) stop();
    };
    audio.play().then(
      () => started(session),
      () => {
        if (current === session) stop();
      }
    );
  };

  const playWithBuffer = (session, url) => {
    const c = getCtx();
    if (c.state !== 'running') c.resume(); // doit être appelé pendant le clic (iOS)
    loadBuffer(url)
      .then((buffer) => {
        if (current !== session) return;
        const source = c.createBufferSource();
        source.buffer = buffer;
        source.loop = true;
        source.connect(c.destination);
        source.start(0);
        const t0 = c.currentTime;
        session.stop = () => {
          try {
            source.stop();
          } catch (e) {}
          source.disconnect();
        };
        session.progress = () => ((c.currentTime - t0) % buffer.duration) / buffer.duration;
        started(session);
      })
      .catch(() => {
        // Repli (ex. CORS) : lecteur audio HTML classique en boucle
        if (current === session) playWithElement(session, url, true);
      });
  };

  const toggle = (row) => {
    const wasPlaying = current && current.row === row;
    stop();
    const url = row.dataset.src;
    if (wasPlaying || !url) return;

    const session = { row };
    current = session;
    setState(row, 'loading');
    pauseVideos();
    watch(row.closest('loop-player') || row.closest('.card-wrapper') || row);

    const loop = row.hasAttribute('data-loop');
    if (loop && AC) playWithBuffer(session, url);
    else playWithElement(session, url, loop);
  };

  // Waveforms pré-calculées : 1 caractère (0-63) par point, dessinées en SVG vectoriel
  const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_';
  const WF_HEIGHT = 34;

  const drawWaveform = (el) => {
    const code = el.dataset.peaks;
    if (!code || el.firstChild) return;
    const mid = WF_HEIGHT / 2;
    const top = [];
    const bottom = [];
    for (let i = 0; i < code.length; i++) {
      const amp = Math.max(0.6, (ALPHABET.indexOf(code[i]) / 63) * mid);
      top.push(`${i},${(mid - amp).toFixed(1)}`);
      bottom.unshift(`${i},${(mid + amp).toFixed(1)}`);
    }
    const last = code.length - 1;
    const d = `M0,${mid}L${top.join('L')}L${last},${mid}L${bottom.join('L')}Z`;
    el.innerHTML =
      `<svg viewBox="0 0 ${last} ${WF_HEIGHT}" preserveAspectRatio="none" focusable="false">` +
      `<path class="dlp__wf-base" d="${d}"/><path class="dlp__wf-fill" d="${d}"/></svg>`;
  };

  class LoopPlayer extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('.dlp__wf[data-peaks]').forEach(drawWaveform);

      this.addEventListener('click', (event) => {
        const more = event.target.closest('.dlp__more');
        if (more) {
          const expanded = this.toggleAttribute('data-expanded');
          more.setAttribute('aria-expanded', expanded);
          if (!more.dataset.labelMore) more.dataset.labelMore = more.textContent.trim();
          more.textContent = expanded ? more.dataset.labelLess : more.dataset.labelMore;
          if (!expanded && current && current.row.hasAttribute('data-more')) stop();
          return;
        }
        if (event.target.closest('a')) return;
        const row = event.target.closest('.dlp__track');
        if (row && this.contains(row)) toggle(row);
      });

      // Précharge une loop dès qu'on la survole à la souris pour un démarrage instantané
      this.addEventListener('pointerover', (event) => {
        if (event.pointerType !== 'mouse' || !AC) return;
        const row = event.target.closest('.dlp__track[data-loop]');
        if (row && row.dataset.src) loadBuffer(row.dataset.src);
      });
    }

    disconnectedCallback() {
      if (current && this.contains(current.row)) stop();
    }
  }

  // Bouton play sur les vignettes produit (même moteur : une seule lecture à la fois)
  class LoopCardPlay extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      if (!button) return;
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        toggle(this);
      });
      button.addEventListener('pointerover', (event) => {
        if (event.pointerType === 'mouse' && AC && this.dataset.src) loadBuffer(this.dataset.src);
      });
    }

    disconnectedCallback() {
      if (current && current.row === this) stop();
    }
  }

  // Une vidéo démarre avec du son (démo Looper, vidéo produit…) : le player s'arrête.
  // Les vidéos d'ambiance muettes en autoplay ne coupent rien.
  const isOtherMedia = (el) => el instanceof HTMLMediaElement && el !== audio;
  document.addEventListener(
    'play',
    (event) => {
      if (isOtherMedia(event.target) && audible(event.target)) stop();
    },
    true
  );
  document.addEventListener(
    'volumechange',
    (event) => {
      const media = event.target;
      if (isOtherMedia(media) && !media.paused && audible(media)) stop(); // on remet le son d'une vidéo
    },
    true
  );
  // YouTube / Vimeo : clic sur l'aperçu de la vidéo (Dawn charge l'iframe à ce moment-là)…
  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest && event.target.closest('deferred-media')) stop();
    },
    true
  );
  // …ou lecture dans une vidéo déjà chargée : on écoute l'état du lecteur YouTube / Vimeo (postMessage)
  const listenTo = (frame) => {
    const win = frame.contentWindow;
    if (!win) return;
    if (/youtube/i.test(frame.src)) win.postMessage('{"event":"listening","id":1,"channel":"widget"}', '*');
    else if (/vimeo/i.test(frame.src)) {
      win.postMessage('{"method":"addEventListener","value":"play"}', '*');
      win.postMessage('{"method":"addEventListener","value":"pause"}', '*');
    }
  };
  document.addEventListener(
    'load',
    (event) => {
      if (event.target.tagName === 'IFRAME') listenTo(event.target);
    },
    true
  );
  document.querySelectorAll('iframe').forEach(listenTo);
  const videoStates = new WeakMap(); // lecteur vidéo -> dernier état connu (on ne coupe qu'au passage en lecture)
  window.addEventListener('message', (event) => {
    if (!/^https:\/\/(www\.)?(youtube\.com|youtube-nocookie\.com|player\.vimeo\.com)$/.test(event.origin)) return;
    let data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return;
      }
    }
    if (!data || !event.source) return;
    let state = null;
    if (data.event === 'onStateChange') state = data.info;
    else if (data.event === 'infoDelivery' && data.info && 'playerState' in data.info) state = data.info.playerState;
    else if (data.event === 'play') state = 1;
    else if (data.event === 'pause') state = 2;
    if (state === null) return;
    const before = videoStates.get(event.source);
    videoStates.set(event.source, state);
    if (state === 1 && before !== 1) stop();
  });
  // Clic dans l'iframe (si le navigateur ne transmet pas l'état) : le focus passe dans la vidéo
  window.addEventListener('blur', () => {
    setTimeout(() => {
      const frame = document.activeElement;
      if (frame && frame.tagName === 'IFRAME' && /youtube|vimeo/i.test(frame.src)) stop();
    }, 0);
  });

  customElements.define('loop-player', LoopPlayer);
  customElements.define('loop-card-play', LoopCardPlay);
})();
